import { useEffect, useMemo, useState } from 'react';

import { FaHome } from "react-icons/fa";
import { FaTicketAlt } from "react-icons/fa";
import { BiSolidOffer } from 'react-icons/bi';
import { MdOutlineSupportAgent } from "react-icons/md";
import { HiUsers } from "react-icons/hi2";
import { MdOutlineStorefront } from "react-icons/md";
import { MdOutlineSettingsApplications } from "react-icons/md";

import PetstopLogo from '../assets/icons/PetstopLogo';
import useIsMobile, {
  DESKTOP_SIDEBAR_WIDTH,
  MOBILE_SIDEBAR_WIDTH
} from '../hooks/useIsMobile';

const MENU_SECTIONS = [
  {
    key: 'home',
    label: 'Home',
    icon: FaHome,
    route: 'home'
  },
  {
    key: 'offers',
    label: 'Offers & Promotions',
    icon: BiSolidOffer,
    items: [
      { label: 'Webstore Links', route: '/webstore-links' },
      { label: 'Promotions', route: '/offers-and-promotions' },
      { label: 'Coupons', route: '/nonvalidated-coupons' }
    ]
  },
  {
    key: 'business',
    label: 'Business',
    icon: MdOutlineStorefront,
    items: [
      { label: 'Business Locations', route: '/business-locations' }
    ]
  },
  {
    key: 'users',
    label: 'Users & Engagement',
    icon: HiUsers,
    items: [
      { label: 'Users', route: '/users-users' },
      { label: 'Push Notifications', route: '/users-users' }
    ]
  },
  {
    key: 'coupons',
    label: 'Coupons',
    icon: FaTicketAlt,
    items: [
      { label: 'Coupons', route: '/coupons' },
      { label: 'Lookup', route: '/coupons/lookup' }
    ]
  },
  {
    key: 'app-settings',
    label: 'App Settings',
    icon: MdOutlineSettingsApplications,
    items: [
      { label: 'Files', route: '/app-files' }
    ]
  },
  {
    key: 'support',
    label: 'Customer Support',
    icon: MdOutlineSupportAgent
  }
];

const SideNavBar = (props) => {
  const isMobile = useIsMobile();
  const [activeMobileSection, setActiveMobileSection] = useState(null);

  const mobileDrawerSection = useMemo(() => {
    if (!activeMobileSection) {
      return null;
    }

    return MENU_SECTIONS.find((section) => section.key === activeMobileSection) ?? null;
  }, [activeMobileSection]);

  useEffect(() => {
    if (!isMobile) {
      setActiveMobileSection(null);
    }
  }, [isMobile]);

  const handleNavigate = (route) => {
    props.handleUpdate({ updateType : 'navigate', route : route });
    if (isMobile) {
      setActiveMobileSection(null);
    }
  };

  const handleMobileSectionClick = (section) => {
    if (section.route) {
      handleNavigate(section.route);
      return;
    }

    if (!section.items || section.items.length === 0) {
      setActiveMobileSection(null);
      return;
    }

    setActiveMobileSection((prevSection) => {
      if (prevSection === section.key) {
        return null;
      }

      return section.key;
    });
  };

  const renderMenuItems = (items) => {
    return (
      <ul style={ styles.subMenuList }>
        { items.map((item) => {
          const isActive = props.activeRoute === item.route;
          return (
            <li
              key={ item.label }
              className='nav-link text-white'
              style={ isActive ? styles.liMenuItemActive : styles.liMenuItem }
            >
              <a onClick={ () => { handleNavigate(item.route); } }>{ item.label }</a>
            </li>
          );
        })}
      </ul>
    );
  };

  if (isMobile) {
    return (
      <>
        { mobileDrawerSection ? (
          <div style={ styles.mobileOverlay } onClick={ () => { setActiveMobileSection(null); }} />
        ) : null }

        <div className="sidebar d-flex flex-column" style={ styles.mobileSidebar }>
          <div style={ styles.mobileLogoContainer }>
            <PetstopLogo width={ 42 } />
          </div>

          <ul className="list-unstyled components" style={ styles.mobileNavList }>
            { MENU_SECTIONS.map((section) => {
              const IconComponent = section.icon;
              const isSelected = activeMobileSection === section.key || (section.route === 'home' && props.activeRoute === '/');

              return (
                <li key={ section.key } style={ styles.mobileNavItem }>
                  <button
                    type="button"
                    onClick={ () => { handleMobileSectionClick(section); } }
                    style={{
                      ...styles.mobileIconButton,
                      ...(isSelected ? styles.mobileIconButtonActive : {})
                    }}
                    aria-label={ section.label }
                  >
                    <IconComponent size='1.45em' />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        { mobileDrawerSection ? (
          <div style={ styles.mobileDrawer }>
            <div style={ styles.mobileDrawerHeader }>
              <span style={ styles.mobileDrawerTitle }>{ mobileDrawerSection.label }</span>
              <button
                type="button"
                onClick={ () => { setActiveMobileSection(null); } }
                style={ styles.mobileDrawerCloseButton }
                aria-label="Close menu"
              >
                ×
              </button>
            </div>
            { mobileDrawerSection.items ? (
              renderMenuItems(mobileDrawerSection.items)
            ) : (
              <div style={ styles.mobileDrawerEmptyState }>No submenu items</div>
            ) }
          </div>
        ) : null }
      </>
    );
  }

  return (
    <div className="sidebar d-flex flex-column p-3" style={ styles.desktopSidebar }>
      <div className="sidebar-header" style={ styles.desktopLogoContainer }>
        <PetstopLogo width={ 140 } />
      </div>
      <ul className="list-unstyled components" style={ styles.desktopNavList }>
        { MENU_SECTIONS.map((section, index) => {
          const IconComponent = section.icon;
          const sectionContent = (
            <div style={ styles.menuItemContainer }>
              <div style={ styles.menuItemIconContainer }>
                <IconComponent size={ section.key === 'users' || section.key === 'app-settings' ? '1.6em' : '1em' } />
              </div>
              <div style={ styles.menuItemText }>{ section.label }</div>
            </div>
          );

          return (
            <>
              <li key={ `${section.key}-item` } className='nav-link text-white'>
                <a onClick={ () => {
                  if (section.route) {
                    handleNavigate(section.route);
                  }
                }}>
                  { sectionContent }
                </a>
                { section.items ? renderMenuItems(section.items) : null }
              </li>
              { index < MENU_SECTIONS.length - 1 ? (
                <li key={ `${section.key}-divider` } style={ styles.liMenuDivider }><div className="divider"></div></li>
              ) : null }
            </>
          );
        })}
        <li style={{ height : 200 }} />
      </ul>
    </div>
  );
};

const styles = {
  desktopSidebar : {
    backgroundColor : '#009fe3',
    width : DESKTOP_SIDEBAR_WIDTH,
    height : '100%',
    position : 'fixed',
    top : 5,
    left : 5,
    zIndex : 1000,
    borderRadius: 5,
    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.4)',
    maxHeight : '100%',
    overflowY : 'auto',
    scrollBehavior : 'smooth',
    scrollbarWidth : 'none',
    msOverflowStyle : 'none'
  },
  desktopLogoContainer : {
    display : 'flex',
    alignItems : 'center',
    justifyContent : 'center',
    width : '100%',
    marginTop : -15
  },
  desktopNavList : {
    cursor : 'pointer'
  },
  mobileSidebar : {
    backgroundColor : '#009fe3',
    width : MOBILE_SIDEBAR_WIDTH,
    height : 'calc(100% - 10px)',
    position : 'fixed',
    top : 5,
    left : 5,
    zIndex : 1200,
    borderRadius : 5,
    boxShadow : '0px 0px 10px rgba(0, 0, 0, 0.4)',
    alignItems : 'center',
    paddingTop : 12,
    paddingBottom : 12
  },
  mobileLogoContainer : {
    display : 'flex',
    justifyContent : 'center',
    width : '100%',
    marginBottom : 18
  },
  mobileNavList : {
    display : 'flex',
    flexDirection : 'column',
    alignItems : 'center',
    gap : 14,
    width : '100%',
    margin : 0,
    padding : 0
  },
  mobileNavItem : {
    width : '100%',
    display : 'flex',
    justifyContent : 'center'
  },
  mobileIconButton : {
    width : 44,
    height : 44,
    borderRadius : 12,
    border : 'none',
    backgroundColor : 'transparent',
    color : 'white',
    display : 'flex',
    alignItems : 'center',
    justifyContent : 'center'
  },
  mobileIconButtonActive : {
    backgroundColor : 'rgba(255, 255, 255, 0.2)'
  },
  mobileOverlay : {
    position : 'fixed',
    inset : 0,
    backgroundColor : 'rgba(0, 0, 0, 0.2)',
    zIndex : 1100
  },
  mobileDrawer : {
    position : 'fixed',
    top : 5,
    left : MOBILE_SIDEBAR_WIDTH + 12,
    height : 'calc(100% - 10px)',
    width : 'min(250px, calc(100vw - 95px))',
    backgroundColor : '#009fe3',
    zIndex : 1250,
    borderRadius : 5,
    boxShadow : '0px 0px 10px rgba(0, 0, 0, 0.35)',
    padding : 18,
    overflowY : 'auto',
    transform : 'translateX(0)',
    transition : 'transform 0.2s ease-out'
  },
  mobileDrawerHeader : {
    display : 'flex',
    alignItems : 'center',
    justifyContent : 'space-between',
    marginBottom : 16
  },
  mobileDrawerTitle : {
    color : 'white',
    fontWeight : 'bold',
    fontSize : '1rem'
  },
  mobileDrawerCloseButton : {
    border : 'none',
    backgroundColor : 'transparent',
    color : 'white',
    fontSize : '1.8rem',
    lineHeight : 1
  },
  mobileDrawerEmptyState : {
    color : 'white',
    opacity : 0.8,
    fontSize : '0.9rem'
  },
  subMenuList : {
    paddingLeft: 30
  },
  liMenuDivider : {
    paddingBottom : 5
  },
  liMenuItem : {
    cursor : 'pointer',
    fontSize: '0.9em'
  },
  liMenuItemActive : {
    cursor : 'pointer',
    marginRight : 20,
    fontWeight : 'bold',
    fontSize: '0.9em'
  },
  menuItemContainer : {
    display : 'flex',
    flexDirection : 'row',
    alignItems : 'center',
    justifyContent : 'center'
  },
  menuItemIconContainer : {
    display : 'flex',
    alignItems : 'center',
    width : 20
  },
  menuItemText : {
    marginLeft : 5,
    fontSize: '0.9em',
    color : 'white',
    fontWeight : 'bold',
    width : '100%'
  }
};

export default SideNavBar;
